import React, { useState } from "react";
import { Modal, Button, Form, Badge, Spinner } from "react-bootstrap";
import { ListingDetailEntity } from "../dto/listing/ListingDetailEntity";
import { suspendListing } from "../controller/ListingController";
import toast from "react-hot-toast";

interface Props {
    show: boolean;
    listingDetail: ListingDetailEntity;
    onClose: () => void;
    onSuccess: () => void;
}

export const ListingSuspendModal: React.FC<Props> = ({ show, listingDetail, onClose, onSuccess }) => {
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);

    const listing = listingDetail.listing;

    const handleClose = () => {
        if (loading) return;
        setReason("");
        onClose();
    };

    const handleSubmit = async () => {
        if (!listing?.id || !reason.trim()) return;
        setLoading(true);
        try {
            const response = await suspendListing(listing.id, reason.trim());
            if (response.status !== 0) {
                toast.error(response.msg);
            } else {
                toast.success(`Listing #${listing.id} suspended.`);
                handleClose();
                onSuccess();
            }
        } catch (e: any) {
            toast.error(e.message ?? "Unexpected error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered backdrop={loading ? "static" : true}>
            <Modal.Header style={{ background: "#fff3cd", borderBottom: "2px solid #ffc107" }}>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: "1.3rem" }}>⏸️</span>
                    <span style={{ fontWeight: 700, color: "#856404" }}>Suspend Auction</span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Listing summary */}
                <div style={{
                    background: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: 8,
                    padding: "12px 16px",
                    marginBottom: 16,
                }}>
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-1">
                        <div>
                            <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                                Listing #{listing?.id}
                            </div>
                            <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                                {listing?.productTitle ?? "—"}
                            </div>
                        </div>
                        <Badge bg="success">{listing?.status}</Badge>
                    </div>
                    <div className="mt-2" style={{ fontSize: "0.85rem", color: "#495057" }}>
                        Seller: <strong>{listing?.accountNickname ?? listing?.accountPhoneNo ?? "—"}</strong>
                        &nbsp;·&nbsp; Bids: <strong>{listing?.bidCount ?? 0}</strong>
                    </div>
                </div>

                <p style={{ fontSize: "0.88rem", color: "#495057" }}>
                    Suspending will freeze the auction. No wallet changes occur at this step.
                    You can then <strong>cancel</strong> or <strong>restore</strong> it from the detail page.
                </p>

                <Form.Group>
                    <Form.Label style={{ fontWeight: 600 }}>
                        Reason <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={500}
                        placeholder="e.g. Suspected fraudulent listing, under review…"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={loading}
                    />
                    <Form.Text className="text-muted">{reason.length}/500</Form.Text>
                </Form.Group>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Go Back
                </Button>
                <Button
                    variant="warning"
                    onClick={handleSubmit}
                    disabled={!reason.trim() || loading}
                >
                    {loading
                        ? <><Spinner size="sm" animation="border" className="me-2" />Suspending…</>
                        : "Suspend Auction"
                    }
                </Button>
            </Modal.Footer>
        </Modal>
    );
};